import { Helmet } from "react-helmet-async"
import { Lightbulb, Target, Scale } from "lucide-react"
import { Badge } from "../../components/ui/badge"
import { useState, useEffect } from "react";
import SEO from "../SEO"
export default function VisionMission() {
const visionMissionSchema = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://pranaamhospitals.com/#organization",
    "name": "Pranaam Hospitals",
    "url": "https://pranaamhospitals.com/",
    "logo": "https://pranaamhospitals.com/images/pranaam-logo.png",
    "description": "Explore the vision, mission, and core values of Pranaam Hospitals, focused on delivering compassionate, high-quality healthcare with excellence, integrity, and patient-centric care in Hyderabad..",
    "sameAs": [
      "https://www.facebook.com/pranaamhospitals",
      "https://www.instagram.com/pranaamhospitals"
    ]
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": "https://pranaamhospitals.com/vision-mission#breadcrumb",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://pranaamhospitals.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Vision & Mission",
        "item": "https://pranaamhospitals.com/vision-mission"
      }
    ]
   }
];

const [seo, setSeo] = useState({});
  const [pageKeywords, setPageKeywords] = useState("");
const [pageData, setPageData] = useState(null);
useEffect(() => {
  fetch("/wp-json/custom/v1/page-seo?slug=vision-mission")
  .then(res => res.json())
  .then(data => { if (data?.seo) setSeo(data.seo); setPageKeywords(data?.keywords || ""); })
  .catch(() => {});
}, []);

  return (
    <>
      <SEO
        title={seo.title || "Vision & Mission | Pranaam Hospitals"}
        description={seo.description || "Learn about the vision and mission of Pranaam Hospitals and our commitment to delivering compassionate, patient-centric, and world-class healthcare services in Hyderabad."}
        canonical={seo.canonical || "https://pranaamhospitals.com/vision-mission"}
        robots={Array.isArray(seo.robots) ? seo.robots.join(", ") : seo.robots || "index, follow"}
        keywords={pageKeywords}
        ogTitle={seo.og_title}
        ogDescription={seo.og_description}
        ogImage={seo.og_image || seo.featured_image}
        twitterTitle={seo.twitter_title}
        twitterDescription={seo.twitter_description}
        author="Pranaam Hospitals"
        publisher="Pranaam Hospitals"
        schema={visionMissionSchema}
      />
      
      <main className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-r from-primary/10 to-primary/5 py-16 md:py-24">
          <div className="container px-4 mx-auto">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-4 px-3 py-1">Our Purpose</Badge>
              <h1 className="mb-6 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
                Vision & <span className="text-primary">Mission</span>
              </h1>
              <p className="mb-8 text-lg text-gray-700 max-w-3xl mx-auto">
                Our journey is guided by a commitment to excellence and a mission to provide compassionate, high-quality healthcare to every individual.
              </p>
            </div>
          </div>
        </section>

        <div className="container mx-auto px-4 py-8 md:py-16">
          <div className="max-w-6xl mx-auto">
            
            <div className="grid md:grid-cols-3 gap-8">
              
              {/* Vision */}
              <div className="bg-white rounded-lg shadow-lg p-6 md:p-8">
                <div className="flex justify-center mb-6">
                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-full border-4 border-red-600 flex items-center justify-center">
                    <Lightbulb className="w-10 h-10 md:w-12 md:h-12 text-red-600" />
                  </div>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-red-600 mb-6 text-center">VISION</h2>
                <p className="text-gray-700 leading-relaxed text-center">
                  To be the go-to destination for excellence and innovation in healthcare.
                </p>
              </div>

              {/* Mission */}
              <div className="bg-white rounded-lg shadow-lg p-6 md:p-8">
                <div className="flex justify-center mb-6">
                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-full border-4 border-red-600 flex items-center justify-center">
                    <Target className="w-10 h-10 md:w-12 md:h-12 text-red-600" />
                  </div>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-red-600 mb-6 text-center">MISSION</h2>
                <p className="text-gray-700 leading-relaxed text-center">
                  Nothing is more rewarding than the heart-felt gratitude from our patients and their loves when we help restore their health and live better lives. This helps us set the bar even higher for ourselves by delivering compassionate and seamless care at all times.
                </p>
              </div>

              {/* Values */}
              <div className="bg-white rounded-lg shadow-lg p-6 md:p-8">
                <div className="flex justify-center mb-6">
                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-full border-4 border-red-600 flex items-center justify-center">
                    <Scale className="w-10 h-10 md:w-12 md:h-12 text-red-600" />
                  </div>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-red-600 mb-6 text-center">VALUES</h2>
                <p className="text-gray-700 mb-4 text-center">
                  The core values ingrained deep in our system, drives us to offer the best healthcare services through:
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span>Trust</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span>Commitment</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span>Compassion</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span>Excellence</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span>Integrity</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span>Respect</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span>Teamwork</span>
                  </li>
                </ul>
              </div>

            </div>
          </div>
        </div>
      </main>
    </>
  )
}
